import { toRaw } from 'vue';

import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import { getAuthErrorMessage, IamClient } from '@evidentid/iam-client';
import { IamClientUser } from '@evidentid/iam-client/types';
import { RbacRole, RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
import { toRbacRole } from '@evidentid/tprm-portal-lib/utils/common/toRbacRole';

import { clearChatBotSessionData } from '../../online-chat/utils/session-states/clearChatBotSessionData';
import { usePersistingErrorStore } from '../../persisting-error/store';
import { ErrorObject } from '../../persisting-error/types';
import { useSnackbarStore } from '../../snackbar/store';
import { UserState, RelyingParty } from '../types';

export interface UserAppRequirements {
    auth: IamClient;
    gatewayClient: GatewayApiClient;
}

function appOf(store: { $app: any }): UserAppRequirements {
    return store.$app;
}

export const useUserStore = defineStore('user', {
    state: (): UserState => ({
        initialized: false,
        duringPasswordReset: false,
        duringSsoLogin: null,
        duringRegularLogin: false,
        duringRegistration: false,
        sendingEmailVerification: false,
        data: null,
        relyingParties: null,
        currentRpRole: null,
        currentRpRbacFeatures: null,
    }),
    actions: {
        updatePasswordResetStatus(status: boolean) {
            this.duringPasswordReset = status;
        },
        updateSsoLoginStatus(method: string | null) {
            this.duringSsoLogin = method;
        },
        updateRegularLoginStatus(status: boolean) {
            this.duringRegularLogin = status;
        },
        updateRegistrationStatus(status: boolean) {
            this.duringRegistration = status;
        },
        updateEmailVerificationStatus(status: boolean) {
            this.sendingEmailVerification = status;
        },
        updateRelyingParties(relyingParties: RelyingParty[] | null) {
            this.relyingParties = relyingParties;
        },
        updateUserData(data: IamClientUser | null) {
            this.initialized = true;
            this.data = data;
        },
        updateCurrentRpRole(role: RbacRole | null) {
            this.currentRpRole = role;
        },
        updateCurrentRpRbacFeatures(features: Record<RbacFeature, boolean> | null) {
            this.currentRpRbacFeatures = features;
        },
        async showLoginError(error: any) {
            const message = getAuthErrorMessage(error);
            useSnackbarStore().displaySnackbar({ permanent: true, message });
            console.warn('Login error', error);
        },
        async showRegistrationError(error: any) {
            const message = getAuthErrorMessage(error);
            useSnackbarStore().displaySnackbar({ permanent: true, message });
            console.warn('Registration error', error);
        },
        async showResetError(error: any) {
            const message = getAuthErrorMessage(error, {
                InvalidEmailError: 'The email appears incorrect.',
            });
            useSnackbarStore().displaySnackbar({ permanent: true, message });
            console.warn('Reset error', error);
        },
        async logInWithSso(method: string) {
            try {
                this.updateSsoLoginStatus(method);
                await appOf(this).auth.login(method);
            } catch (error) {
                await this.showLoginError(error);
            } finally {
                this.updateSsoLoginStatus(null);
            }
        },
        async logIn(payload: { email: string, password: string }) {
            const { email, password } = payload;
            try {
                this.updateRegularLoginStatus(true);
                await appOf(this).auth.login('credentials', email, password);
            } catch (error) {
                await this.showLoginError(error);
            } finally {
                this.updateRegularLoginStatus(false);
            }
        },
        async resetPassword(email: string) {
            const signInUrl = Object.assign(new URL(window.location.href), { hash: '#/auth' }).toString();
            try {
                this.updatePasswordResetStatus(true);
                await appOf(this).auth.resetPassword(email, signInUrl);
                useSnackbarStore().displaySnackbar({
                    message: 'Thanks! Check your email for the link.',
                    permanent: true,
                    success: true,
                });
            } catch (error) {
                await this.showResetError(error);
            } finally {
                this.updatePasswordResetStatus(false);
            }
        },
        async register(payload: { email: string, password: string }) {
            const { email, password } = payload;
            try {
                this.updateRegistrationStatus(true);
                await appOf(this).auth.register('credentials', email, password);
                await this.sendEmailVerification();
            } catch (error) {
                await this.showRegistrationError(error);
            } finally {
                this.updateRegistrationStatus(false);
            }
        },
        async logOut() {
            try {
                await appOf(this).auth.logOut();

                // Clear authentication-related errors after log out
                await this.clearAuthorizationError();
                clearChatBotSessionData();
            } catch (error) {
                console.warn('Log out error', error);
            }
        },
        async sendEmailVerification() {
            const signInUrl = Object.assign(new URL(window.location.href), { hash: '#/' }).toString();
            try {
                this.updateEmailVerificationStatus(true);
                await appOf(this).auth.sendEmailVerification(signInUrl);
                useSnackbarStore().displaySnackbar({
                    message: 'Thanks! Check your email for the link.',
                    success: true,
                });
            } catch (error) {
                if (error?.code === 'auth/too-many-requests') {
                    useSnackbarStore().displaySnackbar({
                        message: 'The email has been sent recently. If it will not be delivered in few minutes, try again.',
                        permanent: true,
                        success: false,
                    });
                } else {
                    await usePersistingErrorStore().showError(error);
                }
            } finally {
                this.updateEmailVerificationStatus(false);
            }
        },
        async refreshSession() {
            await appOf(this).auth.getIdToken(true);
        },
        async clearAuthorizationError() {
            const errorStore = usePersistingErrorStore();
            if ((errorStore.error as ErrorObject | null)?.reason === 'no-relying-parties') {
                await errorStore.showError(null);
            }
        },
        async handleUserDataUpdate(data: IamClientUser | null) {
            if (data === null) {
                this.updateUserData(null);
                this.updateRelyingParties(null);
            } else {
                this.updateUserData(data);
                try {
                    const rps = data.verified
                        ? await appOf(this).gatewayClient.tprmUi().getAvailableRelyingParties()
                        : null;
                    // Handle race condition: do not update relying parties, when there was
                    // already another update. `this.data` is a reactive Proxy of the stored
                    // object, so compare via toRaw to keep reference identity with the raw arg.
                    if (toRaw(this.data) === data) {
                        this.updateRelyingParties(rps?.map((rp) => ({
                            name: rp.commonName,
                            displayName: rp.displayName,
                            role: toRbacRole(rp.userRole) || toRbacRole('admin'),
                            collateralsEnabled: rp.collateralsEnabled,
                            contractExtractionEnabled: rp.contractExtractionEnabled || false,
                            producerFlowEnabled: rp.producerFlowEnabled || false,
                            goAppEnabled: rp.goAppEnabled ?? rp.counterEnabled ?? false,
                        })) || []);
                    }
                } catch (error) {
                    await usePersistingErrorStore().showError(error);
                }
            }
        },
    },
});
